import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBack } from '../ui/NavBar.jsx'
import { Screen, ScreenTitle, PrimaryButton, SecondaryButton, ErrorNote } from '../ui/Screen.jsx'
import { auth as authApi } from '../api.js'
import { logout } from '../auth.js'
import { queryClient } from '../queryClient.js'
import { clearCachedConfig, currentBundle, screensOf } from '../config.js'

/**
 * Account settings, and the way out of the study.
 *
 * FINDABLE, NOT PROMINENT. The consent form promises "you can stop at any time
 * without giving a reason", and until now the only way to act on that was to
 * stop opening the app — which does not stop the emails, so someone who wanted
 * out kept being contacted six times a day for five days.
 *
 * So it lives here rather than in the beep flow: someone looking for it finds
 * it in the obvious place, and someone not looking for it is never nudged
 * toward quitting. It is never on a crisis screen.
 *
 * Withdrawal is irreversible from the participant's side, so it is confirmed
 * rather than instant — an accidental tap is a confirmation problem, and
 * backend deliberately made un-withdrawing something only the study team can do.
 */
export default function Settings() {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)

  async function withdraw() {
    setFailure(null)
    setBusy(true)
    try {
      const result = await authApi.withdraw()
      /*
       * TAKE THE COPY WHILE WE STILL HAVE IT.
       *
       * config owns these words (CFG-30 screens.withdrawal), but the ending
       * must render with NO SESSION AND NO API CALL — after this every
       * endpoint 401s and login 403s, so anything that fetched would bounce
       * her to a sign-in form, which is the opposite of an ending. And the
       * line below deliberately wipes the cached bundle.
       *
       * So the strings are read here, one statement before the wipe, and
       * carried forward. Reading them after would find nothing.
       */
      const withdrawalCopy = screensOf(currentBundle())?.withdrawal ?? null
      // Everything cached belongs to a study this participant is no longer in.
      // Withdrawal is the case that matters most for the on-disk copy: they are
      // 403'd out and can never return, so anything left in localStorage is left
      // there permanently, on a phone that may not be solely theirs.
      queryClient.clear()
      clearCachedConfig()
      /*
       * The ending renders from what we hold, because after this every other
       * endpoint 401s and login 403s.
       *
       * FACTS, NOT PROSE. `data_retained` is the boolean the server has always
       * sent and the client never read; `copy` is config's wording. The screen
       * used to decide whether to state retention by testing whether the
       * server's English sentence contained the substring "kept" — see
       * Withdrawn.jsx for why that was a defect and not a shortcut.
       */
      navigate('/withdrawn', {
        replace: true,
        state: { dataRetained: result?.data_retained, copy: withdrawalCopy },
      })
    } catch (err) {
      setFailure(err)
      setBusy(false)
    }
  }

  return (
    <Screen nav={{ left: <NavBack /> }}>
      <ScreenTitle>Settings</ScreenTitle>

      <div className="mt-2">
        <SecondaryButton
          onClick={async () => {
            await logout()
            navigate('/login', { replace: true })
          }}
        >
          Sign out
        </SecondaryButton>
        <p className="mt-2 text-sm text-[var(--ink-700)]">
          You can sign back in whenever you like. Your check-ins carry on.
        </p>
      </div>

      <hr className="my-8 border-[var(--line)]" />

      <section>
        <h2 className="t-section text-[var(--ink-900)]">Leaving Editrics</h2>
        <p className="mt-2 text-base leading-relaxed text-[var(--ink-700)]">
          Taking part is voluntary and you can stop at any time, without giving a reason.
        </p>

        {!confirming ? (
          <div className="mt-4">
            <SecondaryButton onClick={() => setConfirming(true)}>Leave Editrics</SecondaryButton>
          </div>
        ) : (
          <div className="mt-4 rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-raised)] p-4">
            <p className="text-base font-semibold text-[var(--ink-900)]">
              Are you sure you want to leave?
            </p>

            {/*
              Honest about both halves, because they are different things.
              health_data is a separate affirmation from take_part precisely so
              someone can leave the study without that being read as withdrawing
              permission to hold what they already gave. Nothing here implies
              deletion, and nothing offers it: that route would say "ask us" and
              there is no address to send them to.
            */}
            <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-base leading-relaxed text-[var(--ink-700)]">
              <li>Your check-ins stop straight away. No more reminders.</li>
              <li>
                The answers you have already given are kept, and are still used in the research.
              </li>
              <li>This cannot be undone from the app.</li>
            </ul>

            <ErrorNote error={failure} />

            <div className="mt-5 flex flex-col gap-3">
              <PrimaryButton onClick={withdraw} disabled={busy}>
                {busy ? 'Leaving…' : 'Yes, leave Editrics'}
              </PrimaryButton>
              <SecondaryButton onClick={() => setConfirming(false)} disabled={busy}>
                Stay in Editrics
              </SecondaryButton>
            </div>
          </div>
        )}
      </section>
    </Screen>
  )
}
